import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { auditTime, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { AttentionsFacade } from './attentions.facade';
import { CaseDateFilters } from './cases.facade';
import { AttentionsQPS } from '../core/services/attentions.service';
import { Attention } from '../shared/models/attention';
import { AttentionType } from '../core/enums/attentionType';
import { AttentionsState } from '../core/states/attentions.state';
import { ModulesState } from '../core/states/modules.state';

interface MonthAttentions {
    classic: Attention[];
    module: Attention[];
}

interface CachedMonthAttentions {
    data: MonthAttentions;
    loadedAt: number;
}

interface WindowRequest {
    caseId: number;
    types: AttentionType[];
    months: string[];
    anchor: string;
}

@Injectable({
    providedIn: 'root',
})
export class AttentionsTimelineService {
    private readonly CACHE_TTL_MS = 15 * 60 * 1000;
    private readonly cache = new Map<number, Map<string, CachedMonthAttentions>>();
    private readonly inflight = new Map<string, Observable<MonthAttentions>>();
    private readonly pending = new Map<string, BehaviorSubject<boolean>>();
    private readonly pendingByMonth = new Map<string, BehaviorSubject<boolean>>();
    private readonly activeAttentionsSubject = new BehaviorSubject<Attention[]>([]);
    private readonly anyPendingSubject = new BehaviorSubject<boolean>(false);
    private navToken = 0;
    private activeWindow: { caseId: number; types: AttentionType[]; dateRange: CaseDateFilters; anchor?: Date } | null = null;

    readonly activeAttentions$ = this.activeAttentionsSubject.asObservable();
    readonly anyPending$ = this.anyPendingSubject.asObservable();

    constructor(
        private attentionsFacade: AttentionsFacade,
        private attentionsState: AttentionsState,
        private modulesState: ModulesState,
    ) { }

    setWindow(
        caseId: number,
        types: AttentionType[],
        dateRange: CaseDateFilters,
        anchor?: Date,
    ): Observable<void> {
        this.activeWindow = { caseId, types, dateRange, anchor };
        const request = this.createWindowRequest(caseId, types, dateRange, anchor);
        const token = ++this.navToken;
        const caseCache = this.cache.get(caseId) ?? new Map<string, CachedMonthAttentions>();
        this.cache.set(caseId, caseCache);

        this.emitWindowFromCache(request, caseCache, token);

        const loads = request.months.map(month => this.loadMonth(
            request.caseId,
            month,
            request.types,
            month === request.anchor
        ).pipe(
            tap(() => {
                if (token === this.navToken) {
                    this.publishWindow(request, caseCache);
                }
            })
        ));

        return forkJoin(loads).pipe(
            tap(() => {
                if (token === this.navToken) {
                    this.publishWindow(request, caseCache);
                }
            }),
            auditTime(30),
            tap(() => this.updatePendingState()),
            map(() => undefined),
        );
    }

    isLoadingMonth$(monthKey: string): Observable<boolean> {
        const subject = this.pendingByMonth.get(monthKey) ?? new BehaviorSubject<boolean>(false);
        this.pendingByMonth.set(monthKey, subject);
        return subject.asObservable();
    }

    invalidateCase(caseId: number | null): void {
        if (caseId == null) {
            return;
        }

        this.cache.delete(caseId);
        if (this.activeWindow?.caseId === caseId) {
            this.navToken++;
        }

        const invalidatedKeys = new Set([
            ...Array.from(this.inflight.keys()),
            ...Array.from(this.pending.keys()),
        ]);

        Array.from(invalidatedKeys)
            .filter(key => key.startsWith(`${caseId}|`))
            .forEach(key => {
                this.inflight.delete(key);
                this.pending.delete(key);
            });
        this.updateAllMonthPendingStates();
        this.updatePendingState();
    }

    refreshActiveCase(caseId: number): Observable<void> {
        const window = this.activeWindow;
        this.invalidateCase(caseId);
        return window?.caseId === caseId
            ? this.setWindow(caseId, window.types, window.dateRange, window.anchor)
            : of(undefined);
    }

    deactivateCase(caseId: number | null): void {
        if (this.activeWindow?.caseId === caseId) {
            this.activeWindow = null;
        }
        this.invalidateCase(caseId);
    }

    cleanStore(): void {
        this.cache.clear();
        this.inflight.clear();
        this.pending.clear();
        this.pendingByMonth.clear();
        this.navToken++;
        this.activeWindow = null;
        this.activeAttentionsSubject.next([]);
        this.anyPendingSubject.next(false);
    }

    private createWindowRequest(
        caseId: number,
        types: AttentionType[],
        dateRange: CaseDateFilters,
        anchor?: Date,
    ): WindowRequest {
        const start = moment(dateRange.historyFromDate);
        const end = moment(dateRange.historyToDate);
        const anchorMonth = moment(anchor ?? dateRange.historyFromDate).format('YYYY-MM');
        const months: string[] = [];

        if (dateRange.mode === 'month' || !dateRange.mode) {
            months.push(
                start.clone().subtract(1, 'month').format('YYYY-MM'),
                start.format('YYYY-MM'),
                start.clone().add(1, 'month').format('YYYY-MM'),
            );
        } else {
            const cursor = start.clone().startOf('month');
            const last = end.clone().startOf('month');
            while (cursor.isSameOrBefore(last, 'month')) {
                months.push(cursor.format('YYYY-MM'));
                cursor.add(1, 'month');
            }
        }

        return {
            caseId,
            types: [...types].sort(),
            months: Array.from(new Set(months)),
            anchor: anchorMonth,
        };
    }

    private emitWindowFromCache(request: WindowRequest, caseCache: Map<string, CachedMonthAttentions>, token: number): void {
        const anchor = this.getCachedMonth(caseCache, request.anchor, request.types);
        if (anchor && token === this.navToken) {
            this.publishWindow(request, caseCache);
        }
    }

    private loadMonth(
        caseId: number,
        month: string,
        types: AttentionType[],
        interactive: boolean,
    ): Observable<MonthAttentions> {
        const key = this.cacheKeyForCase(caseId, month, types);
        const caseCache = this.cache.get(caseId);
        const cached = caseCache ? this.getCachedMonth(caseCache, month, types) : null;
        if (cached) {
            return of(cached);
        }

        const existing = this.inflight.get(key);
        if (existing) {
            return existing;
        }

        const fromDate = moment(month, 'YYYY-MM').startOf('month').toDate();
        const toDate = moment(month, 'YYYY-MM').endOf('month').toDate();
        const qps: AttentionsQPS = { casesIds: [caseId], fromDate, toDate, types };
        const pendingSubject = this.pending.get(key) ?? new BehaviorSubject<boolean>(false);
        pendingSubject.next(true);
        this.pending.set(key, pendingSubject);
        const monthPendingSubject = this.pendingByMonth.get(month) ?? new BehaviorSubject<boolean>(false);
        monthPendingSubject.next(true);
        this.pendingByMonth.set(month, monthPendingSubject);

        const request$ = this.attentionsFacade.loadAttentionsByMonth(qps, { interactive }).pipe(
            tap(result => {
                if (this.cache.get(caseId) !== caseCache) {
                    return;
                }
                caseCache.set(this.cacheKey(month, types), {
                    data: result,
                    loadedAt: Date.now(),
                });
            }),
            finalize(() => {
                if (this.inflight.get(key) === request$) {
                    pendingSubject.next(false);
                    this.pending.delete(key);
                    this.inflight.delete(key);
                }
                this.updateMonthPendingState(month);
                this.updatePendingState();
            }),
            shareReplay(1),
        );
        this.inflight.set(key, request$);
        return request$;
    }

    private publishWindow(request: WindowRequest, caseCache: Map<string, CachedMonthAttentions>): void {
        const months = request.months.map(month => this.getCachedMonth(caseCache, month, request.types));
        const classic = this.uniqueAttentions(months.flatMap(value => value?.classic ?? []));
        const module = this.uniqueAttentions(months.flatMap(value => value?.module ?? []));

        this.attentionsState.setAttentions(classic);
        this.attentionsState.setAttentionsCaseActive([...classic, ...module]);
        this.modulesState.setModuleAttentions(module);
        this.activeAttentionsSubject.next([...classic, ...module]);
    }

    private getCachedMonth(
        caseCache: Map<string, CachedMonthAttentions>,
        month: string,
        types: AttentionType[],
    ): MonthAttentions | null {
        const cached = caseCache.get(this.cacheKey(month, types));
        if (!cached) {
            return null;
        }

        if (Date.now() - cached.loadedAt >= this.CACHE_TTL_MS) {
            return null;
        }

        return cached.data;
    }

    private uniqueAttentions(attentions: Attention[]): Attention[] {
        const byId = new Map<number, Attention>();
        attentions.forEach(attention => {
            if (attention?.id != null) {
                byId.set(Number(attention.id), attention);
            }
        });
        return Array.from(byId.values());
    }

    private cacheKey(month: string, types: AttentionType[]): string {
        return `${month}|${types.join(',')}`;
    }

    private cacheKeyForCase(caseId: number, month: string, types: AttentionType[]): string {
        return `${caseId}|${this.cacheKey(month, types)}`;
    }

    private updatePendingState(): void {
        this.anyPendingSubject.next(Array.from(this.pending.values()).some(subject => subject.value));
    }

    private updateMonthPendingState(month: string): void {
        const subject = this.pendingByMonth.get(month);
        if (!subject) {
            return;
        }

        subject.next(Array.from(this.pending.entries())
            .some(([key, pendingSubject]) => key.includes(`|${month}|`) && pendingSubject.value));
    }

    private updateAllMonthPendingStates(): void {
        this.pendingByMonth.forEach((_, month) => this.updateMonthPendingState(month));
    }
}
